import type { de } from "./de";

export const fr: typeof de = {
  seo: {
    ogLocale: "fr_FR",
    home: {
      title: "La semaine de 4 jours dans la banque | Livre blanc 2026",
      description: "Comment les banques résolvent la pénurie de talents grâce à la semaine de 4 jours : études de cas, dividende de l'IA et attractivité employeur. Livre blanc gratuit.",
    },
    businessCase: {
      title: "Demander un business case | Semaine de 4 jours dans la banque",
      description: "Business case individuel sur la semaine de 4 jours pour votre banque : faites votre demande et recevez une analyse sur mesure d'Intraprenör.",
    },
  },
  hero: {
    badge: "Livre blanc 2026",
    title1: "La semaine de 4 jours",
    title2: "dans le secteur bancaire",
    subtitle: "Entre attractivité employeur et dividende de l'IA : comment les banques résolvent la pénurie de talents grâce à la semaine de 4 jours",
    downloadButton: "Télécharger le livre blanc",
    learnMore: "En savoir plus",
  },
  stats: {
    title: "Les chiffres",
    titleItalic: "parlent d'eux-mêmes",
    subtitle: "Les études internationales et les exemples pratiques le montrent : la semaine de 4 jours n'est plus une expérience.",
    items: [
      { suffix: "%", label: "Poursuite", description: "des entreprises du projet pilote allemand poursuivent la semaine de 4 jours" },
      { suffix: "x", label: "Plus de candidatures", description: "Multiplication par cinq des candidatures à la PSD Bank Braunschweig" },
      { suffix: "%", label: "Moins de jours de maladie", description: "Réduction des absences pour maladie à l'Atom Bank (UK)" },
    ],
  },
  cases: {
    title1: "Les pionniers de la semaine de 4 jours :",
    title2: "4 cas bancaires qui montrent",
    titleItalic: "comment ça peut fonctionner",
    subtitle: "Ces banques le prouvent : la semaine de 4 jours fonctionne – de la néobanque digitale à la caisse d'épargne traditionnelle.",
    resultsLabel: "Résultats",
    items: [
      {
        type: "Le Pionnier Digital",
        highlight: "68 % d'arrêts maladie en moins",
        model: "Semaine de 4 jours à salaire complet depuis 2021",
        results: [
          
          "49% d'augmentation des candidatures",
          "68% de jours de maladie en moins",
          "Rotation du personnel en baisse de 57%",
        ],
      },
      {
        type: "La Banque Coopérative Régionale",
        highlight: "Tous les postes pourvus",
        model: "Semaine de 4 jours en projet pilote",
        results: [
          "Multiplication par cinq des candidatures",
          "Tous les postes vacants pourvus avec succès",
          "Réduction significative des jours de maladie",
          "Haute satisfaction des employés",
        ],
        quote: {
          text: "Avant, nous avions à peine de réactions à nos offres d'emploi. Aujourd'hui, nous recevons 50 à 60 candidatures pour chaque annonce.",
          author: "Carsten Graf",
          role: "Président du directoire, PSD Bank Braunschweig",
        },
      },
      {
        type: "L'Innovatrice Solide",
        highlight: "Employeur le plus attractif de la région",
        model: "Semaine de 4 jours depuis 2024",
        results: [
          "Transferts d'employés de grandes banques (Credit Suisse, UBS)",
          "Employeur le plus attractif en zone rurale",
          "Productivité maintenue au même niveau",
          "Fort positionnement dans la concurrence pour les talents",
        ],
      },
      {
        type: "La Grande Caisse d'Épargne Flexible",
        highlight: "Liberté de choix pour les employés",
        model: "Semaine de 4 jours dans le cadre du concept 'WertSchätze'",
        results: [
          "Semaine de 4 jours proposée comme option",
          "Partie d'un concept global de reconnaissance",
          "Modèles de temps de travail flexibles pour différentes phases de vie",
          "Fidélisation accrue des employés",
        ],
      },
    ],
  },
  trends: {
    badge: "4 Facteurs",
    title: "Pourquoi la semaine de 4 jours",
    titleItalic: "fonctionne",
    subtitle: "Quatre facteurs clés font de la semaine de 4 jours un modèle de réussite pour les banques.",
    items: [
      {
        title: "Attractivité & fidélisation des employés",
        subtitle: "La révolution du recrutement",
        stats: [
          { value: "5x", label: "Plus de candidatures" },
          { value: "57%", label: "Moins de rotation" },
        ],
        description: "La semaine de 4 jours est l'outil le plus efficace contre la pénurie de talents dans le secteur bancaire. Les banques rapportent une multiplication par cinq des candidatures et une réduction drastique de la rotation.",
        insight: "Aucun employé ne nous a quittés – Hans-Jürgen Lembicz, Volksbank Euskirchen",
      },
      {
        title: "Santé des employés",
        subtitle: "Moins de stress, plus de bien-être",
        stats: [
          { value: "68%", label: "Moins de jours de maladie" },
          { value: "89 min", label: "Moins de stress/semaine" },
        ],
        description: "Le temps de repos supplémentaire entraîne des améliorations mesurables de la santé physique et mentale. Les jours de maladie diminuent drastiquement, les taux de burnout reculent.",
        insight: "Les employés récupèrent en moyenne 89 minutes de temps de travail moins stressant par semaine.",
      },
      {
        title: "Coûts & efficacité",
        subtitle: "Le business case",
        stats: [
          { value: "65%", label: "Processus optimisés" },
          { value: "100%", label: "Productivité maintenue" },
        ],
        description: "Moins de temps de travail ne signifie pas moins de résultats. Grâce à l'optimisation des processus et une concentration accrue, la productivité reste au même niveau.",
        insight: "65% des entreprises pilotes allemandes ont éliminé les réunions, tâches et processus inutiles grâce à la semaine de 4 jours.",
      },
      {
        title: "Intelligence artificielle",
        subtitle: "Le dividende de l'IA",
        stats: [
          { value: "3,5", label: "Jours selon JPMorgan" },
          { value: "30%", label: "Gain de temps potentiel" },
        ],
        description: "L'automatisation par l'IA dans le secteur bancaire crée de la marge pour des horaires réduits. Le PDG de JPMorgan, Jamie Dimon, prédit : la semaine de 3,5 jours arrivera dans une génération.",
        insight: "L'IA et l'automatisation permettent de restituer les gains de productivité sous forme de temps libre aux employés.",
      },
    ],
  },
  quotes: {
    title: "Témoignages",
    titleItalic: "du terrain",
    subtitle: "Ce que les dirigeants rapportent de leur expérience avec la semaine de 4 jours.",
    items: [
      {
        text: "Aucun employé ne nous a quittés. Normalement, nous avons une rotation d'environ cinq pour cent, mais en ce moment elle est à zéro.",
        author: "Hans-Jürgen Lembicz",
        role: "Directeur",
        company: "Volksbank Euskirchen",
      },
      {
        text: "Dès le premier semestre, notre productivité a augmenté. Aucune perte, au contraire : plus de concentration, plus d'attractivité en tant qu'employeur.",
        author: "Hans Arnet",
        role: "CEO",
        company: "Raiffeisenbank Luzerner Landschaft Nordwest",
      },
      {
        text: "Avant, nous avions à peine de réactions à nos offres d'emploi. Aujourd'hui, nous recevons 50 à 60 candidatures pour chaque annonce – dans tous les domaines.",
        author: "Carsten Graf",
        role: "Président du directoire",
        company: "PSD Bank Braunschweig",
      },
    ],
  },
  dataTable: {
    title: "Banques dans la région DACH",
    subtitle: "Aperçu de toutes les banques avec des initiatives de semaine de 4 jours en Allemagne, Autriche et Suisse.",
    headers: { bank: "Banque", country: "Pays", model: "Modèle", hours: "Heures", status: "Statut", result: "Résultat" },
    statusLabels: { aktiv: "Actif", pilot: "Pilote", geplant: "Planifié" },
    items: [
      { name: "Atom Bank", country: "UK", model: "4 jours à salaire complet", hours: "34h", status: "aktiv" as const, highlight: "68 % d'arrêts maladie en moins" },
      { name: "PSD Bank Braunschweig", country: "DE", model: "Semaine de 4 jours ou\nsemaine de 4,5 jours", hours: "32-36h", status: "aktiv" as const, highlight: "Tous les postes pourvus" },
      { name: "Raiffeisenbank Luzerner Landschaft Nordwest", country: "CH", model: "Semaine de 4 jours", hours: "38h", status: "aktiv" as const, highlight: "Rotation plus faible" },
      { name: "Raiffeisenbank Surselva", country: "CH", model: "Semaine de 4 jours", hours: "38h", status: "aktiv" as const, highlight: "Attractivité accrue" },
      { name: "Raiffeisenbank Appenzeller Hinterland", country: "CH", model: "Semaine de 4 jours ou\nsemaine de 4,5 jours", hours: "38h", status: "aktiv" as const, highlight: "Candidatures reçues d'employés de grandes banques" },
      { name: "Sparkasse zu Lübeck", country: "DE", model: "Modèle WertSchätze", hours: "Flexible", status: "aktiv" as const, highlight: "Liberté de choix pour tous" },
      { name: "Volksbank Euskirchen", country: "DE", model: "Semaine de 4 jours", hours: "35h", status: "aktiv" as const, highlight: "0% de rotation" },
      { name: "Volksbank Kaiserslautern", country: "DE", model: "Semaine de 4 jours", hours: "36h", status: "pilot" as const, highlight: "Projet pilote en cours" },
      { name: "Eckernförder Bank", country: "DE", model: "4 jours optionnel", hours: "32-40h", status: "aktiv" as const, highlight: "Modèle flexible" },
      { name: "VR Bank Niebüll", country: "DE", model: "Semaine de 4 jours", hours: "34h", status: "pilot" as const, highlight: "Haute satisfaction des employés" },
    ],
  },
  download: {
    title: "Livre blanc",
    titleSuffix: " à télécharger",
    subtitle: "Recevez le livre blanc complet avec toutes les études de cas, statistiques et le guide de mise en œuvre de la semaine de 4 jours dans le secteur bancaire.",
    bulletPoints: [
      "Études de cas détaillées de toutes les banques pionnières",
      "Statistiques et résultats de recherche",
      "Guide de mise en œuvre en 3 phases",
      "Calculs de ROI et business case",
    ],
    formLabels: {
      name: "Nom *",
      namePlaceholder: "Jean Dupont",
      email: "Adresse e-mail *",
      emailPlaceholder: "jean@exemple.fr",
      company: "Entreprise (optionnel)",
      companyPlaceholder: "Votre banque / entreprise",
      position: "Poste (optionnel)",
      positionPlaceholder: "ex. Direction, RH",
      submit: "Télécharger gratuitement",
      submitting: "Envoi en cours...",
      disclaimer: "En téléchargeant, vous acceptez notre politique de confidentialité.",
    },
    success: {
      title: "Merci beaucoup !",
      description: "Cliquez sur le bouton pour télécharger le livre blanc en PDF :",
      downloadButton: "Télécharger le PDF",
    },
    whitepaperLanguageNote: "🌐 Le livre blanc est disponible uniquement en allemand",
    toast: {
      successTitle: "Merci beaucoup !",
      successDescription: "Votre téléchargement va bientôt commencer.",
      errorTitle: "Erreur",
      errorDescription: "Un problème est survenu. Veuillez réessayer.",
    },
    validation: {
      nameMin: "Le nom doit comporter au moins 2 caractères",
      emailInvalid: "Veuillez entrer une adresse e-mail valide",
    },
  },
  event: {
    badge: "Webinaire en direct",
    languageNote: "En allemand",
    title: "Plus de productivité dans la banque grâce à des horaires réduits",
    lead: "Pendant que Thomas Groß (Helaba) réclame plus de travail et que Christian Sewing (Deutsche Bank) appelle à plus d'engagement, d'autres banques livrent déjà des résultats – avec moins de jours travaillés.",
    date: "Mar. 5 mai 2026, 12h00",
    context: "La pénurie de talents et le changement démographique frappent durement les banques, en particulier en zone rurale. Les modèles de travail flexibles ne sont plus un « nice-to-have », mais un instrument stratégique pour rester un employeur pertinent – tout en délivrant des résultats.",
    contextProof: "La PSD Bank Braunschweig a relevé son objectif de volume de crédit à 130 millions d'euros – avec la semaine de 4 jours. La Raiffeisenbank Appenzeller Hinterland a été l'une des premières banques de Suisse à piloter ce modèle – avec des effets mesurables sur la satisfaction des employés et l'attractivité employeur.",
    speakersTitle: "Nos invités",
    speakers: [
      { name: "Simon Frehner", role: "Président de la direction, Raiffeisenbank Appenzeller Hinterland" },
      { name: "Carsten Graf", role: "Porte-parole du directoire, PSD Bank Braunschweig" },
    ],
    hostsLabel: "Animation",
    hosts: [
      { name: "Carsten Meier", role: "Initiative Semaine de 4 jours Allemagne", url: "https://www.4tagewoche.org" },
      { name: "Veit Hailperin", role: "Semaine de 4 jours Suisse & Autriche", url: "https://www.hailperin.ch" },
    ],
    agendaTitle: "Au programme",
    agenda: [
      "Comment se passe réellement la mise en œuvre dans le secteur bancaire ?",
      "Quels bénéfices pour le recrutement et la fidélisation – et lesquels non ?",
      "Quels modèles flexibles fonctionnent, lesquels échouent ?",
      "Questions sans détour sur la productivité, l'absentéisme et l'expérience client",
    ],
    audience: "Pour les directoires, directions générales et responsables RH des banques et institutions financières.",
    duration: "60 min.",
    format: "En ligne",
    price: "Participation gratuite",
    signupButton: "S'inscrire gratuitement",
    linkedinButton: "Voir sur LinkedIn",
  },
  contact: {
    fazitTitle: "Le bon moment pour des décisions audacieuses",
    fazitText: "La semaine de 4 jours n'est plus une vision d'avenir – c'est une réalité vécue dans les banques tournées vers l'avenir. Qu'il s'agisse d'une réponse stratégique à la pénurie de talents, d'un investissement dans la santé des employés ou d'une préparation à la transformation par l'IA : c'est le bon moment.",
    phasesTitle: "Votre chemin vers la semaine de 4 jours",
    phases: [
      { number: "01", title: "Atelier de management", description: "Analyse de la situation de départ et développement d'un concept sur mesure" },
      { number: "02", title: "Phase pilote", description: "Introduction accompagnée dans des domaines sélectionnés avec évaluation continue" },
      { number: "03", title: "Déploiement", description: "Extension progressive à l'ensemble de l'entreprise avec mesure du succès" },
    ],
    ctaTitle: "Prêt pour la prochaine étape ?",
    ctaText: "Prenez rendez-vous pour un entretien sans engagement et découvrez comment la semaine de 4 jours peut aussi fonctionner dans votre banque.",
    downloadButton: "Télécharger le livre blanc",
    contacts: [
      { name: "Carsten Meier", role: "Semaine de 4 jours Allemagne" },
      { name: "Veit Hailperin", role: "Semaine de 4 jours Suisse & Semaine de 4 jours Autriche" },
    ],
    impressum: "Mentions légales",
    datenschutz: "Protection des données",
    copyright: "© 2026 4tagewoche.org – Conseil en innovation du temps de travail",
  },
  businessCase: {
    backToReport: "Retour au rapport",
    badge: "Offre gratuite",
    heroTitle: "La semaine de 4 jours est-elle rentable pour mon entreprise ?",
    heroSubtitle: "Nous calculons votre business case bancaire individuel – basé sur des données, spécifique au secteur et sans préjugés.",
    intro1: "Sur la base de notre expérience issue de nombreux projets de conseil et de nos données sur les banques dans la région DACH, nous élaborons un business case individuel pour votre institut.",
    intro2: "Lors d'un appel de 30 minutes, nous souhaitons mieux comprendre votre situation. Par la suite, nous vous fournirons un business case sur mesure pour discuter en interne, en toute connaissance de cause, si une semaine de 4 jours a du sens pour vous.",
    benefits: [
      { title: "Appel de 30 minutes", desc: "Nous comprenons votre situation, vos objectifs et vos préoccupations." },
      { title: "Business case individuel", desc: "Des chiffres concrets pour votre institut – productivité, recrutement, absentéisme." },
      { title: "Base de discussion", desc: "Un document avec lequel vous pouvez travailler au conseil d'administration." },
    ],
    formTitle: "Demander un business case",
    formSubtitle: "Parlez-nous brièvement de votre situation – nous vous répondrons dans les deux jours ouvrables.",
    fields: {
      name: "Nom",
      namePlaceholder: "Prénom et nom",
      position: "Fonction",
      positionPlaceholder: "p. ex. direction, responsable RH",
      company: "Entreprise",
      companyPlaceholder: "Nom de votre institut",
      situation: "Situation actuelle",
      situationPlaceholder: "Comment se présente votre situation actuelle en matière de personnel et de temps de travail ? Quels défis vous préoccupent ?",
      perspective: "Votre perspective actuelle sur la semaine de 4 jours",
      perspectivePlaceholder: "Quelle est votre position aujourd'hui ? Quelles sont vos questions ouvertes, quelles sont vos préoccupations ?",
      privacy: "J'accepte la politique de protection des données et consens à ce que mes informations soient enregistrées pour le traitement de la demande.",
      submit: "Demander un business case",
      submitting: "Envoi en cours …",
    },
    validation: {
      nameMin: "Veuillez indiquer votre nom.",
      positionMin: "Veuillez indiquer votre fonction.",
      companyMin: "Veuillez indiquer votre entreprise.",
      situationMin: "Veuillez décrire votre situation en au moins une phrase.",
      perspectiveMin: "Veuillez décrire votre perspective en au moins une phrase.",
      privacyRequired: "Veuillez accepter la politique de protection des données.",
    },
    success: {
      title: "Merci pour votre demande !",
      description: "Nous vous contacterons dans les deux jours ouvrables pour convenir d'un appel de 30 minutes.",
      backButton: "Retour au rapport",
    },
    toast: {
      successTitle: "Demande envoyée",
      successDescription: "Nous vous contacterons sous peu.",
      errorTitle: "Une erreur s'est produite",
      errorDescription: "Veuillez réessayer ou nous écrire directement par e-mail.",
    },
  },
};
